import { z } from "zod"

export const zodResolver = <Input = unknown, Output = unknown>(scheme: z.ZodType<Output>) => {
  const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
    switch (issue.code) {
      case z.ZodIssueCode.too_small:
        return { message: issue.minimum === 1 ? "必須項目です。" : `${issue.minimum}文字以上で入力してください。` }
      case z.ZodIssueCode.too_big:
        return { message: `${issue.maximum}文字以下で入力してください。` }
    }

    return { message: ctx.defaultError }
  }

  return (value: Input): ValidationResult => {
    const result = scheme.safeParse(value, { errorMap: customErrorMap })
    if (!result.success) return result.error.format()._errors
  }
}

export const refinements = {
  required<T>(value: T | null, ctx: z.RefinementCtx) {
    if (value === null || (typeof value === "string" && value.trim().length === 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "必須項目です。" })
    }
  },
  // 旧パスワードポリシー。現在は strongPassword に移行済み。
  onlyAscii(value: string, ctx: z.RefinementCtx) {
    const regex = /^[\u0021-\u007e]*$/
    if (!regex.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "半角英数字記号のみで入力してください。" })
    }
  },
  /** パスワードポリシーを定義するバリデーション（フロントエンド）。<br>
   * バックエンド側のパスワードポリシーは UserPassword.java で定義。
   */
  strongPassword(value: string, ctx: z.RefinementCtx) {
    const regex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!-/:-@[-`{-~])[!-~]{8,256}$/
    if (!regex.test(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "半角英大文字・小文字・数字・記号をそれぞれ1文字以上含む8文字以上で入力してください。",
      })
    }
  },
  onlyFullWidthKatakana(value: string, ctx: z.RefinementCtx) {
    const regex = /^[\u30A1-\u30FE]*$/
    if (!regex.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "全角カタカナのみで入力してください。" })
    }
  },
  noHalfWidthKatakana(value: string, ctx: z.RefinementCtx) {
    const regex = /^[^\uff61-\uff9f]*$/
    if (!regex.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "半角カタカナは使用できません。" })
    }
  },
  noPlatformDependentChar(value: string, ctx: z.RefinementCtx) {
    const regex =
      /^[^①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳ⅠⅡⅢⅣⅤⅥⅦⅧⅨⅩ㍉㌔㌢㍍㌘㌧㌃㌶㍑㍗㌍㌦㌣㌫㍊㌻㎜㎝㎞㎎㎏㏄㎡㍻〟№㏍℡㊤㊥㊦㊧㊨㈱㈲㈹㍾㍽㍼]*$/
    if (!regex.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "機種依存文字は使用できません。" })
    }
  },
  onlyHalfWidthTelNo(value: string, ctx: z.RefinementCtx) {
    const regex = /^[0-9-]*$/
    if (!regex.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "半角数字とハイフンのみで入力してください。" })
    }
  },
  // 郵便番号のバリデーション（半角数字のみ）
  onlyHalfWidthDigitZipCode(value: string, ctx: z.RefinementCtx) {
    const regex = /^\d{7}$/
    if (!regex.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "半角数字7桁で入力してください。" })
    }
  },
  // 空白文字、全角スペース、カンマ、セミコロンで区切られたメールアドレスのバリデーション
  emailList(value: string, ctx: z.RefinementCtx) {
    const addressList = value.split(/[\s　,;]+/).filter((item) => item.trim() !== "")
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    for (const address of addressList) {
      if (!regex.test(address)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "スペースで区切られたメールアドレスの形式で入力してください。",
        })
        break
      }
    }
  },
} as const

export const validator = {
  required: zodResolver<unknown>(z.unknown().superRefine(refinements.required)),
}

type ValidationResult = readonly string[] | undefined
